// Внесение в чёрный список. Панель, а не модалка: заведение записи обратимо — лишнюю запись
// снимают, а не отменяют навсегда (правило 4). Необратимо здесь только стирание, и оно живёт
// отдельной модалкой в карточке.
//
// ## Два входа в одну форму
//
// Из списка — «с нуля»: человека вносят по бумаге, и в системе его может не быть вовсе. Из
// карточки кандидата — с подставленными ФИО, датой рождения и телефоном: рекрутёр уже смотрит
// на этого человека, и перенабирать его фамилию вручную значит однажды ошибиться буквой, а по
// ошибочной букве сверка не сработает никогда.
//
// Подставленное остаётся ПРАВИМЫМ: в карточке кандидата данные с площадки, а в чёрный список
// пишут то, что стоит в документах.
//
// ## Почему форма не пускает запись без единого признака человека
//
// Сервер такую запись примет: обязательны только причина и правовое основание. Но искать по
// ней некого — сверка сравнивает телефон, фамилию с датой рождения, удостоверение и паспорт, и
// при пустых полях не сработает ни один признак. То есть запись будет, а человека она не
// остановит, и узнается это в тот день, когда его снова возьмут на объект.

import type { FormEvent } from 'react';
import { useState } from 'react';

import { RiErrorWarningLine, RiPhoneLine, RiUser3Line } from '@remixicon/react';

import * as Button from '@/shared/ui/button';
import { DateField } from '@/shared/ui/date-field';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Hint from '@/shared/ui/hint';
import { Skeleton } from '@/shared/ui/screen-state';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';
import { TextArea } from '@/shared/ui/text-area';
import { TextField } from '@/shared/ui/text-field';

import type { BlacklistEntryIn, BlacklistReason } from '../api/blacklist';
import { BLACKLIST_REASONS } from '../api/blacklist';
import { GateNote, NoPermissionNote, useCan } from '../components/gates';
import { BLACKLIST_REASON_TITLES } from '../dictionaries';
import { newIdempotencyKey, useCandidateQuery, useCreateEntryMutation } from '../queries';
import { BLANK } from '../route';

/** Внесение записи: право рекрутёра и администратора. */
const EDIT_PERMISSION = 'hiring.blacklist.edit';

/** Столько знаков правового основания требует сервер. */
const MIN_BASIS = 3;

/** Что подставляется в форму из карточки кандидата. Всё остальное человек вводит сам. */
interface Prefill {
  lastName: string;
  firstName: string;
  middleName: string;
  birthDate: string;
  phone: string;
  region: string;
  guardLicenseNo: string;
}

const ПУСТО: Prefill = {
  lastName: '',
  firstName: '',
  middleName: '',
  birthDate: '',
  phone: '',
  region: '',
  guardLicenseNo: '',
};

export function NewEntryPanel({
  candidateId,
  onClose,
}: {
  candidateId: string;
  onClose: () => void;
}) {
  // Разделение на два компонента не косметическое: за карточкой кандидата ходит хук, и звать
  // его для мастера «с нуля» значило бы запрашивать кандидата по слову «new».
  if (candidateId === BLANK) {
    return <Форма prefill={ПУСТО} onClose={onClose} />;
  }
  return <ИзКандидата candidateId={candidateId} onClose={onClose} />;
}

/**
 * Панель, открытая из карточки кандидата. Форма рисуется только после того, как карточка
 * приехала: подставить значения в уже набранную человеком форму нельзя — он к этому моменту
 * успеет что-то напечатать, и подстановка сотрёт напечатанное у него под руками.
 */
function ИзКандидата({ candidateId, onClose }: { candidateId: string; onClose: () => void }) {
  const candidate = useCandidateQuery(candidateId);

  if (candidate.data === undefined && candidate.isPending) {
    return (
      <div className='flex flex-col gap-3'>
        {/* Скелет, а не слово «загружаем…»: серый текст в форме неотличим от подсказки
            под полем, и человек читает его как ответ, а не как ожидание. */}
        <Skeleton className='h-4 w-40' />
        <Skeleton className='h-9 w-full' />
        <Skeleton className='h-9 w-full' />
        <Skeleton className='h-9 w-full' />
      </div>
    );
  }

  const данные = candidate.data;
  return (
    <Форма
      prefill={
        данные === undefined
          ? ПУСТО
          : {
              lastName: данные.last_name ?? '',
              firstName: данные.first_name ?? '',
              middleName: данные.middle_name ?? '',
              birthDate: данные.birth_date ?? '',
              phone: данные.phone_normalized ?? данные.phone_raw ?? '',
              region: данные.region ?? '',
              guardLicenseNo: данные.guard_license_no ?? '',
            }
      }
      // Карточка не приехала — форма всё равно работает, просто пустая. Молчать об этом
      // нельзя: человек нажал «внести» из карточки кандидата и ждёт его данные в полях.
      failedNote={данные === undefined}
      onClose={onClose}
    />
  );
}

type FieldName = 'identity' | 'reason' | 'reason_note' | 'legal_basis';

function Форма({
  prefill,
  failedNote,
  onClose,
}: {
  prefill: Prefill;
  failedNote?: boolean;
  onClose: () => void;
}) {
  const allowed = useCan(EDIT_PERMISSION);

  // Ключ идемпотентности один на форму, а не на нажатие: повторная отправка той же формы
  // (сорвалась сеть, человек нажал второй раз) вернёт уже созданную запись, а не заведёт
  // вторую такую же.
  const [idempotencyKey] = useState(newIdempotencyKey);
  const mutation = useCreateEntryMutation(idempotencyKey);

  const [lastName, setLastName] = useState(prefill.lastName);
  const [firstName, setFirstName] = useState(prefill.firstName);
  const [middleName, setMiddleName] = useState(prefill.middleName);
  const [birthDate, setBirthDate] = useState(prefill.birthDate);
  const [phone, setPhone] = useState(prefill.phone);
  const [passportSeries, setPassportSeries] = useState('');
  const [passportNumber, setPassportNumber] = useState('');
  const [licenseNo, setLicenseNo] = useState(prefill.guardLicenseNo);
  const [region, setRegion] = useState(prefill.region);
  const [reason, setReason] = useState(NO_VALUE);
  const [reasonNote, setReasonNote] = useState('');
  const [legalBasis, setLegalBasis] = useState('');
  const [retentionUntil, setRetentionUntil] = useState('');
  const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({});
  const [gateNote, setGateNote] = useState<string | null>(null);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!allowed) {
      setGateNote(
        'Вносить в чёрный список может рекрутёр или администратор. Попросите открыть это право — заполненная форма никуда не денется.',
      );
      return;
    }
    if (mutation.isPending) {
      setGateNote('Запись уже создаётся, ждём ответ сервера. Второй раз не отправляем.');
      return;
    }

    const next: Partial<Record<FieldName, string>> = {};
    const признаки = [lastName, phone, passportSeries + passportNumber, licenseNo].map((поле) =>
      поле.trim(),
    );
    if (признаки.every((поле) => поле === '')) {
      next.identity =
        'Заполните хотя бы одно: фамилию, телефон, паспорт или номер удостоверения. По записи без единого признака сверка никого не найдёт — человека пропустят как незнакомого.';
    }
    if (reason === NO_VALUE) {
      next.reason = 'Выберите причину: без неё непонятно, за что человек в списке.';
    }
    if (reason === 'other' && reasonNote.trim() === '') {
      next.reason_note =
        'Причина «иное» без пояснения не говорит ничего ни рекрутёру, ни проверяющему. Напишите, что именно произошло.';
    }
    if (legalBasis.trim().length < MIN_BASIS) {
      next.legal_basis =
        'Правовое основание указывает заказчик, система его не придумывает: назовите документ или решение, на котором стоит запись.';
    }
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }

    setErrors({});
    setGateNote(null);
    const body: BlacklistEntryIn = {
      reason: reason as BlacklistReason,
      legal_basis: legalBasis.trim(),
      reason_note: reasonNote.trim() === '' ? null : reasonNote.trim(),
      last_name: илиНичего(lastName),
      first_name: илиНичего(firstName),
      middle_name: илиНичего(middleName),
      birth_date: илиНичего(birthDate),
      phone: илиНичего(phone),
      passport_series: илиНичего(passportSeries),
      passport_number: илиНичего(passportNumber),
      guard_license_no: илиНичего(licenseNo),
      region: илиНичего(region),
      retention_until: илиНичего(retentionUntil),
    };
    mutation.mutate(body, { onSuccess: () => onClose() });
  }

  return (
    <form onSubmit={handleSubmit} noValidate className='flex flex-col gap-3'>
      {!allowed && (
        <NoPermissionNote
          что='вносить в чёрный список'
          кто='Это делают рекрутёр и администратор системы.'
        />
      )}

      {failedNote === true && (
        <Hint.Root hasError>
          <Hint.Icon as={RiErrorWarningLine} />
          Карточку кандидата открыть не удалось, поэтому поля пустые. Форма работает: заполните
          данные руками.
        </Hint.Root>
      )}

      <TextField
        id='new-entry-last-name'
        label='Фамилия'
        icon={RiUser3Line}
        value={lastName}
        onChange={setLastName}
        placeholder='Старцев'
      />
      <TextField
        id='new-entry-first-name'
        label='Имя'
        value={firstName}
        onChange={setFirstName}
        placeholder='Иван'
      />
      <TextField
        id='new-entry-middle-name'
        label='Отчество'
        value={middleName}
        onChange={setMiddleName}
        placeholder='Петрович'
      />
      <DateField
        id='new-entry-birth-date'
        label='Дата рождения'
        value={birthDate}
        onChange={setBirthDate}
        hint='Вместе с фамилией это второй по силе признак сверки: без неё однофамильцы дают только «возможное совпадение».'
      />
      <TextField
        id='new-entry-phone'
        label='Телефон'
        icon={RiPhoneLine}
        type='tel'
        inputMode='tel'
        value={phone}
        onChange={setPhone}
        placeholder='+7 900 123-45-67'
        hint='Самый сильный признак: по совпадению телефона сверка даёт точное совпадение. Пишите как удобно, номер приведётся к единому виду.'
      />

      <div className='grid grid-cols-2 gap-2'>
        <TextField
          id='new-entry-passport-series'
          label='Серия паспорта'
          inputMode='numeric'
          maxLength={16}
          value={passportSeries}
          onChange={setPassportSeries}
          placeholder='6714'
        />
        <TextField
          id='new-entry-passport-number'
          label='Номер паспорта'
          inputMode='numeric'
          maxLength={32}
          value={passportNumber}
          onChange={setPassportNumber}
          placeholder='123456'
        />
      </div>

      <TextField
        id='new-entry-license'
        label='Удостоверение частного охранника'
        value={licenseNo}
        onChange={setLicenseNo}
        placeholder='№ 123456'
      />
      <TextField
        id='new-entry-region'
        label='Регион'
        value={region}
        onChange={setRegion}
        placeholder='Ханты-Мансийский АО'
        hint='Список общий по всей группе юрлиц: регион не прячет запись от других регионов, а помогает отличить однофамильцев.'
      />

      {errors.identity !== undefined && (
        <Hint.Root hasError>
          <Hint.Icon as={RiErrorWarningLine} />
          {errors.identity}
        </Hint.Root>
      )}

      <SelectField
        id='new-entry-reason'
        label='Причина'
        required
        value={reason}
        onChange={setReason}
        options={BLACKLIST_REASONS.map((code) => ({
          value: code,
          label: BLACKLIST_REASON_TITLES[code],
        }))}
        placeholder='Выберите причину'
        error={errors.reason}
      />

      {/* Уточнение показывается всегда, а не только при «иное»: у кражи и грубого нарушения
          подробности тоже нужны проверяющему. Обязательным оно становится только у «иного» —
          там справочник без пояснения не говорит ничего. */}
      <TextArea
        id='new-entry-reason-note'
        label='Уточнение причины'
        required={reason === 'other'}
        rows={2}
        value={reasonNote}
        onChange={setReasonNote}
        placeholder='Что именно произошло, коротко'
        error={errors.reason_note}
        hint={
          reason === 'other'
            ? 'При причине «иное» обязательно: иначе в записи не останется ни одного слова о том, что случилось.'
            : 'Останется в карточке записи рядом с причиной.'
        }
      />

      <TextArea
        id='new-entry-legal-basis'
        label='Правовое основание'
        required
        rows={2}
        value={legalBasis}
        onChange={setLegalBasis}
        placeholder='Например: акт от 12.08.2026, объяснительная, приказ № 14'
        error={errors.legal_basis}
        hint='Указывает заказчик: система оснований не придумывает.'
      />

      <DateField
        id='new-entry-retention'
        label='Срок хранения'
        value={retentionUntil}
        onChange={setRetentionUntil}
        hint='Можно не заполнять: срок встанет сам, три года от сегодняшнего дня. После этой даты запись уничтожается.'
      />

      {mutation.isError && <ErrorAlert error={mutation.error} />}

      {gateNote !== null && <GateNote>{gateNote}</GateNote>}

      <div className='flex items-center gap-2 pt-1'>
        <Button.Root type='submit' variant='primary' mode='filled' size='small'>
          {mutation.isPending ? 'Вносим…' : 'Внести в чёрный список'}
        </Button.Root>
        <Button.Root type='button' variant='neutral' mode='stroke' size='small' onClick={onClose}>
          Отмена
        </Button.Root>
      </div>
    </form>
  );
}

/** Пустое поле формы — это отсутствие значения, а не пустая строка в записи. */
function илиНичего(значение: string): string | null {
  const текст = значение.trim();
  return текст === '' ? null : текст;
}
